import { createReducer } from '@src/redux/helpers/createReducer';
import {
  TIN_STATUS,
  UPLOAD_DOCUMENT,
  TIN_SUBMISSION,
  UPDATE_SUBMISSION,
  SET_DOCUMENT_ID,
  SET_DOCUMENT_LOADING,
  SET_TRACKING_NUMBER,
  SET_SIGNATURE_CANVAS,
  RETRIEVE_DRAFT,
  DELETE_SESSION,
  RETURN_TO_REGISTRATION,
  DISABLE_SAVEASDRAFT,
  NPI_VALIDATIONS,
} from './constants';
import { parseDocumentId, parseDocumentLoading } from './parsers';

const initialState = {
  tinStatus: {},
  tinSubmission: {},
  uploadedDocument: {},
  documentIds: {},
  isDocumentUploading: {
    'address.primaryServicingProof': false,
    'address.primaryBillingProof': false,
    'address.primaryRemittanceProof': false,
    'address.secondaryServicing[0].proof': false,
    'address.secondaryServicing[1].proof': false,
    'address.secondaryServicing[2].proof': false,
    'address.secondaryServicing[3].proof': false,
    'address.secondaryServicing[4].proof': false,
  },
  trackingNumber: '',
  signatureCanvas: '',
  isDraftDisabled: false,
  tinDraftList: [],
  uploadProofSaga: { isError: false, isLoading: false, isSuccess: false },
  deleteSessionSaga: { isError: false, isLoading: false, isSuccess: false },
  retrieveDraftSaga: { isError: false, isLoading: false, isSuccess: false },
  fetchTinStatusSaga: { isError: false, isLoading: false, isSuccess: false },
  submitTinSaga: { isError: false, isLoading: false, isSuccess: false },
  updateSubmissionSaga: { isError: false, isLoading: false, isSuccess: false },
  validateNPISaga: { isError: false, isLoading: false, isSuccess: false },
};

export const tinReducer = createReducer(initialState, {
  [`${TIN_STATUS}_SUCCESS`]: (state, action) => {
    return {
      ...state,
      tinStatus: { ...state.tin, ...action.payload },
      fetchTinStatusSaga: { isError: false, isLoading: false, isSuccess: true },
    };
  },
  [`${TIN_STATUS}_STARTED`]: (state, action) => {
    return {
      ...state,
      fetchTinStatusSaga: { isError: false, isLoading: true, isSuccess: false },
    };
  },
  [`${TIN_STATUS}_ERROR`]: (state, action) => {
    return {
      ...state,
      fetchTinStatusSaga: { isError: true, isLoading: false, isSuccess: false },
    };
  },
  [`${UPLOAD_DOCUMENT}_STARTED`]: (state, action) => {
    return {
      uploadedDocuments: {},
      uploadProofSaga: { isError: false, isLoading: true, isSuccess: false },
    };
  },
  [`${UPLOAD_DOCUMENT}_SUCCESS`]: (state, action) => {
    return {
      ...state,
      uploadedDocument: { ...state.uploadedDocument, ...action.payload },
      uploadProofSaga: { isError: false, isLoading: false, isSuccess: true },
    };
  },
  [`${UPLOAD_DOCUMENT}_ERROR`]: (state, action) => {
    return {
      ...state,
      uploadProofSaga: { isError: true, isLoading: false, isSuccess: false },
    };
  },
  [`${TIN_SUBMISSION}_STARTED`]: () => {
    return {
      tinSubmission: { tinSubmission: {} },
      submitTinSaga: { isError: false, isLoading: true, isSuccess: false },
    };
  },
  [`${TIN_SUBMISSION}_SUCCESS`]: (state, action) => {
    return {
      ...state,
      tinDraftList: [],
      tinSubmission: { ...state.tinSubmission, ...action.payload },
      submitTinSaga: { isError: false, isLoading: false, isSuccess: true },
    };
  },
  [`${TIN_SUBMISSION}_ERROR`]: (state, action) => {
    return {
      ...state,
      tinSubmission: { tinSubmission: {} },
      submitTinSaga: { isError: true, isLoading: false, isSuccess: false },
    };
  },
  [SET_DOCUMENT_ID]: (state, action) => {
    return {
      documentIds: parseDocumentId(state, action.payload),
    };
  },
  [SET_TRACKING_NUMBER]: (state, action) => {
    return {
      trackingNumber: action.payload,
    };
  },
  [SET_DOCUMENT_LOADING]: (state, action) => {
    return {
      isDocumentUploading: parseDocumentLoading(state, action.payload),
    };
  },
  [SET_SIGNATURE_CANVAS]: (state, action) => {
    return {
      signatureCanvas: action.payload,
    };
  },
  [`${RETRIEVE_DRAFT}_SUCCESS`]: (state, action) => {
    return {
      ...state,
      tinDraft: { ...action.payload },
      tinDraftList: { ...action.payload },
      retrieveDraftSaga: { isError: false, isLoading: false, isSuccess: true },
    };
  },
  [`${RETRIEVE_DRAFT}_ERROR`]: (state, action) => {
    return {
      ...state,
      retrieveDraftSaga: { isError: true, isLoading: false, isSuccess: false },
    };
  },
  [`${RETRIEVE_DRAFT}_STARTED`]: (state, action) => {
    return {
      ...state,
      retrieveDraftSaga: { isError: false, isLoading: true, isSuccess: false },
    };
  },
  [`${DELETE_SESSION}_SUCCESS`]: (state, action) => {
    const tin = Object.values(state.tinDraft);
    tin.splice(action.payload.index, 1);
    return {
      ...state,
      tinDraft: tin,
      tinDraftList: tin,
      deleteSessionSaga: { isError: false, isLoading: false, isSuccess: true },
    };
  },
  [`${DELETE_SESSION}_ERROR`]: (state, action) => {
    return {
      ...state,
      deleteSessionSaga: { isError: true, isLoading: false, isSuccess: false },
    };
  },
  [`${DELETE_SESSION}_STARTED`]: (state, action) => {
    return {
      ...state,
      deleteSessionSaga: { isError: false, isLoading: true, isSuccess: false },
    };
  },
  [RETURN_TO_REGISTRATION]: (state, action) => {
    return {
      ...state,
      tinDraft: {
        ...state.tinDraft,
        returnToRegistration: action.payload,
      },
    };
  },
  [DISABLE_SAVEASDRAFT]: (state, action) => {
    return {
      ...state,
      isDraftDisabled: action.payload,
    };
  },
  [`${UPDATE_SUBMISSION}_STARTED`]: () => {
    return {
      updateSubmission: {},
      updateSubmissionSaga: {
        isError: false,
        isLoading: true,
        isSuccess: false,
      },
    };
  },
  [`${UPDATE_SUBMISSION}_SUCCESS`]: (state, action) => {
    return {
      ...state,
      updateSubmission: { ...action.payload },
      updateSubmissionSaga: {
        isError: false,
        isLoading: false,
        isSuccess: true,
      },
    };
  },
  [`${UPDATE_SUBMISSION}_ERROR`]: (state, action) => {
    return {
      ...state,
      updateSubmission: {},
      updateSubmissionSaga: {
        isError: true,
        isLoading: false,
        isSuccess: false,
      },
    };
  },
  [`${NPI_VALIDATIONS}_STARTED `]: (state, action) => {
    return {
      ...state,
      validateNPISaga: { isError: false, isLoading: true, isSuccess: false },
    };
  },
  [`${NPI_VALIDATIONS}_SUCCESS`]: (state, action) => {
    return {
      ...state,
      validateNPISaga: { isError: false, isLoading: false, isSuccess: true },
    };
  },
  [`${NPI_VALIDATIONS}_ERROR`]: (state, action) => {
    return {
      ...state,
      validateNPISaga: { isError: true, isLoading: false, isSuccess: false },
    };
  },
});
